import { CalendarOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, DatePicker, Form, Input, Modal } from "antd";
import zhCN from "antd/lib/date-picker/locale/zh_CN";
import moment from "moment";
import { FC, useEffect, useState } from "react";
import { getProjectRequirementSummary } from "../../projectRequirement";
import RichTextEditor from "../RichTextEditor";
import "./index.css";

export interface PersonalBoardCardDraft {
  content: string;
  due_time?: number;
  title?: string;
}

export interface PersonalBoardEditableCard extends PersonalBoardCardDraft {
  card_id: string;
}

interface PersonalBoardCardModalProps {
  card?: PersonalBoardEditableCard;
  mode: "create-column" | "create-card" | "edit";
  onCancel: () => void;
  onSubmit: (draft: PersonalBoardCardDraft) => void | Promise<void>;
  open: boolean;
  saving: boolean;
}

interface PersonalBoardCardFormValues {
  due_time?: any;
  title?: string;
}

const PersonalBoardCardModal: FC<PersonalBoardCardModalProps> = ({
  card,
  mode,
  onCancel,
  onSubmit,
  open,
  saving,
}) => {
  const [form] = Form.useForm<PersonalBoardCardFormValues>();
  const [content, setContent] = useState("");
  const [contentError, setContentError] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }
    form.setFieldsValue({
      title: card?.title || "",
      due_time: card?.due_time ? moment(card.due_time) : undefined,
    });
    setContent(card?.content || "");
    setContentError("");
  }, [card, form, open]);

  async function handleFinish(values: PersonalBoardCardFormValues) {
    const summary = getProjectRequirementSummary(content);
    if (!summary.text && !summary.imageCount) {
      setContentError("请输入看板内容或粘贴图片");
      return;
    }
    await onSubmit({
      content: content.trim(),
      ...(values.title?.trim() ? { title: values.title.trim() } : {}),
      ...(values.due_time
        ? { due_time: values.due_time.endOf("day").valueOf() }
        : {}),
    });
  }

  const isEdit = mode === "edit";
  const title = isEdit
    ? "编辑看板卡片"
    : mode === "create-column"
    ? "创建一列看板"
    : "新增看板卡片";

  return (
    <Modal
      centered
      className="personalBoardCardModal"
      destroyOnClose
      footer={null}
      keyboard={false}
      maskClosable={false}
      onCancel={onCancel}
      open={open}
      title={title}
      width={760}
    >
      <header className="personalBoardCardModal__intro">
        <span>{isEdit ? <EditOutlined /> : <PlusOutlined />}</span>
        <div>
          <strong>{title}</strong>
          <small>
            {mode === "create-column"
              ? "新卡片会出现在最右侧的新列中"
              : mode === "create-card"
              ? "新卡片会追加到当前列底部"
              : "更新当前卡片的内容与预期时间"}
          </small>
        </div>
      </header>

      <Form<PersonalBoardCardFormValues>
        className="projectForm personalBoardCardModal__form"
        form={form}
        layout="vertical"
        onFinish={handleFinish}
      >
        <div className="personalBoardCardModal__fields">
          <Form.Item
            label="标题"
            name="title"
            rules={[{ max: 80, message: "标题不能超过 80 个字符" }]}
          >
            <Input maxLength={80} placeholder="选填，例如：本周关注" />
          </Form.Item>
          <Form.Item label="预期结束时间" name="due_time">
            <DatePicker
              format="YYYY年MM月DD日"
              locale={zhCN}
              placeholder="选填预期结束日期"
              suffixIcon={<CalendarOutlined />}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </div>

        <Form.Item
          help={contentError || "支持文字和粘贴图片，单张图片不超过 2MB"}
          label="内容"
          required
          validateStatus={contentError ? "error" : undefined}
        >
          <RichTextEditor
            ariaLabel="看板内容"
            className="personalBoardCardModal__editor"
            onChange={(value) => {
              setContent(value);
              if (contentError) {
                setContentError("");
              }
            }}
            onError={setContentError}
            placeholder="记录想法、事项或直接粘贴图片"
            value={content}
          />
        </Form.Item>

        <footer className="personalBoardCardModal__footer">
          <Button disabled={saving} onClick={onCancel}>
            取消
          </Button>
          <Button htmlType="submit" loading={saving} type="primary">
            {isEdit ? "保存修改" : "完成创建"}
          </Button>
        </footer>
      </Form>
    </Modal>
  );
};

export default PersonalBoardCardModal;
